import React, { useState } from "react";
import DeleteButton from "@/ui-controls/DeleteButton";
import CancelButton from "@/ui-controls/CancelButton";
import TextInput from "@/ui-controls/TextInput";
import { Grid } from "fluentui-react-grid";
import { IconButton, Stack, Text } from "@fluentui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { User, deleteUser as deleteUserApi } from "@/api/users";

interface Props {
  users: User[];
  onDelete?: (users: User[]) => any;
  onClose?: () => any;
}

const DeleteUserForm = (props: Props) => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const isBulk = props.users.length > 1;
  const user = props.users[0];

  const onCancelForm = () => {
    if (props.onClose) props.onClose();
  };

  const handleCopyEmail = async () => {
    if (!user?.EmailAddress) {
      return;
    }
    try {
      await navigator.clipboard.writeText(user.EmailAddress);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = user.EmailAddress;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const removeUser = () => {
    setIsDeleting(true);

    const deleteRequests = props.users.map((u) => deleteUserApi(u.Id));
    Promise.all(deleteRequests)
      .then(
        (results: boolean[]) => {
          if (results.every((result) => result)) {
            if (props.onDelete) props.onDelete(props.users);
          }
        },
        () => {
          handleError();
        },
      )
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: isBulk
        ? Yup.string()
            .required("Confirmation is required")
            .oneOf(["delete"], "Type delete to confirm")
        : Yup.string()
            .required("User email is required")
            .max(250)
            .oneOf([user?.EmailAddress], "Invalid user email"),
    }),
    onSubmit: (values) => {
      removeUser();
    },
  });

  const handleError = () => {};

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Grid dir="ltr">
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              {!isBulk && (
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                  styles={{ root: { marginBottom: "8px" } }}
                >
                  <Text
                    variant="smallPlus"
                    styles={{ root: { fontWeight: 600 } }}
                  >
                    {user?.EmailAddress}
                  </Text>
                  <IconButton
                    iconProps={{ iconName: "Copy" }}
                    title="Copy email"
                    ariaLabel="Copy email"
                    onClick={handleCopyEmail}
                  />
                </Stack>
              )}
              <TextInput
                label={
                  isBulk
                    ? `Enter "delete" to deactivate ${props.users.length} users`
                    : "Confirm User Email"
                }
                baseTypeProps={{
                  disabled: isDeleting,
                  ...formik.getFieldProps("email"),
                }}
                hasError={formik.errors.email && formik.touched.email}
                errorMessage={formik.errors.email}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row className="panel-buttons">
            <Grid.Col>
              <DeleteButton
                text="Deactivate"
                baseTypeProps={{ type: "submit", disabled: isDeleting }}
              />
            </Grid.Col>
            <Grid.Col>
              <CancelButton
                text="Cancel"
                onClick={onCancelForm}
                baseTypeProps={{ disabled: isDeleting }}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </form>
    </>
  );
};

export default DeleteUserForm;
